import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CreditCard, Truck, Printer, Tv, Receipt, Smartphone, Plug, Bike, Globe, FileCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import type { IntegrationItem } from "@/i18n/types";

const GROUP_KEYS = ["payment", "delivery", "equipment", "local"] as const;
type GroupKey = (typeof GROUP_KEYS)[number];

const CARD_META: Record<GroupKey, { icon: React.ElementType; color: string }[]> = {
  payment: [
    { icon: CreditCard, color: "#0085FF" },
    { icon: CreditCard, color: "#32B5FF" },
    { icon: CreditCard, color: "#E8503A" },
    { icon: CreditCard, color: "#7C3AED" },
  ],
  delivery: [
    { icon: Globe, color: "#00adb5" },
    { icon: Bike, color: "#FC3F1D" },
    { icon: Truck, color: "#7C3AED" },
  ],
  equipment: [
    { icon: Printer, color: "#475569" },
    { icon: Receipt, color: "#475569" },
    { icon: Tv, color: "#2563EB" },
  ],
  local: [
    { icon: FileCheck, color: "#10B981" },
    { icon: Smartphone, color: "#0F172A" },
    { icon: Plug, color: "#F59E0B" },
  ],
};

export function Integrations() {
  const { t } = useTranslation();

  return (
    <section id="integrations" className="bg-white py-[96px] overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="text-[32px] sm:text-[48px] leading-[1.15] font-bold tracking-[-0.02em] text-foreground">
            {t("integrations.title")}
          </h2>
          <p className="mt-4 text-foreground-muted text-[17px] sm:text-[18px] max-w-[600px] mx-auto leading-relaxed">
            {t("integrations.subtitle")}
          </p>
        </motion.div>

        <div className="mt-14 space-y-12">
          {GROUP_KEYS.map((key, groupIdx) => {
            const group = t(`integrations.groups.${key}`, { returnObjects: true }) as {
              title: string;
              items: IntegrationItem[];
            };
            const metas = CARD_META[key];
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: groupIdx * 0.1 }}
              >
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-1 h-5 rounded-full bg-gradient-to-b from-blue-500 to-blue-600" />
                  <p className="text-xs font-semibold tracking-wide uppercase text-foreground/45">{group.title}</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.items.map((card, i) => {
                    const { icon: Icon, color } = metas[i % metas.length];
                    return (
                      <motion.div
                        key={card.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.05 }}
                        whileHover={{ y: -4 }}
                        className="h-full"
                      >
                        <Card
                          className={`group/card h-full p-5 rounded-2xl border bg-white hover:shadow-lg transition-all duration-300 overflow-hidden relative ${
                            card.soon ? "opacity-70" : ""
                          }`}
                          style={{ borderColor: "rgba(0,0,0,0.07)" }}
                        >
                          <div
                            className="absolute inset-x-0 top-0 h-0.5 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300"
                            style={{ background: color }}
                          />

                          <div className="flex items-start gap-4 relative z-10">
                            <motion.div
                              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 group-hover/card:scale-105 group-hover/card:shadow-md"
                              style={
                                card.logo
                                  ? { background: "#fff", boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.07)" }
                                  : { background: `${color}12`, color: color }
                              }
                              whileHover={{ scale: 1.05 }}
                            >
                              {card.logo ? (
                                <img src={card.logo} alt="" className="h-8 w-8 rounded-lg object-contain" />
                              ) : (
                                <Icon className="w-5 h-5 transition-transform duration-200" />
                              )}
                            </motion.div>

                            <div className="flex-1 min-w-0">
                              <div
                                className="text-[15px] font-semibold text-foreground leading-tight tracking-tight group-hover/card:text-[color] transition-colors duration-200"
                                style={{ color: "inherit" }}
                              >
                                {card.name}
                              </div>
                              <div className="text-[12.5px] text-foreground/45 mt-1 leading-relaxed line-clamp-2">
                                {card.desc}
                              </div>
                              {card.soon && (
                                <span className="mt-2 inline-flex items-center rounded-full bg-accent/20 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-foreground/60">
                                  {t("integrations.soon")}
                                </span>
                              )}
                            </div>

                            <motion.div
                              initial={{ opacity: 0, x: -5 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ delay: 0.2 }}
                              className="opacity-0 group-hover/card:opacity-100 transition-all duration-200"
                            >
                              <svg
                                className="w-3.5 h-3.5 text-foreground/30"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </motion.div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
